import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPlatforms } from '../../../../services/Platform/Platform.service';

const PLATFORM_COMMISSIONS_STORAGE_KEY = 'platformCommissions';

function loadStoredCommissions() {
  try {
    const storedData = localStorage.getItem(PLATFORM_COMMISSIONS_STORAGE_KEY);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (e) {
    console.error("Failed to parse platform commissions from localStorage", e);
    // Handle error or proceed with empty commissions
  }
  return {};
}

/**
 * Custom hook to fetch platforms, filter them, and load commission rates from localStorage.
 * @returns {{platforms: Array|null, isLoading: boolean, isError: boolean, error: Error|null}}
 */
export function usePlatformsWithCommissions() {
  const storedCommissions = loadStoredCommissions();

  const { data: platformsWithCommissions, isLoading, isError, error } = useQuery({
    queryKey: ['platforms/commissionRate'],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      const platforms = await getPlatforms();
      // Filter out specific platforms like Ulty and Just Eat
      return platforms
              .filter(p => !p.name.includes("Ulty") && !p.name.includes("Just"))
              .map(p => ({
                ...p,
                commissionRate: storedCommissions[p.id] !== undefined ? parseFloat(storedCommissions[p.id]) : null,
              }))
    },
  });

  const queryClient = useQueryClient()

  const handleCommissionRateUpdate = useMutation({
    mutationFn: async({platformId,commissionRate}) => true,
    onMutate : async ({platformId,commissionRate}) => {
      const storedCommissions = loadStoredCommissions();
      
      localStorage.setItem(PLATFORM_COMMISSIONS_STORAGE_KEY, JSON.stringify({
        ...storedCommissions,
        [platformId]: commissionRate
      }));
        queryClient.setQueryData(['platforms/commissionRate'], old => {
          if (!old) return [];
            return [...old.map(p => ({
                ...p,
                ...(p.id === platformId ? {commissionRate}:{})
            }))]
        })
    }
  });

  return {
    platformsWithCommissions: platformsWithCommissions || null, // Return null if still loading or error
    setCommissionRate: handleCommissionRateUpdate.mutate,
    isLoading,
    isError,
    error,
  };
}

// Helper function to save commissions to localStorage (can be used elsewhere)
export function savePlatformCommissions(commissions) {
  try {
    localStorage.setItem(PLATFORM_COMMISSIONS_STORAGE_KEY, JSON.stringify(commissions));
  } catch (e) {
    console.error("Failed to save platform commissions to localStorage", e);
  }
}
